import React from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  Radio, Mic2, Coins, Globe2, Sparkles, Megaphone, Music, Star,
  Bell, Smartphone, Printer, ArrowLeft, CheckCircle2,
} from "lucide-react";

/** Printable A4 tri-fold brochure — two pages, intended for duplex printing.
 *  - Page 1 (outside, what you see when folded): contact panel · "inside-front" panel · cover panel
 *  - Page 2 (inside, when fully unfolded): three audience panels
 *  When previewing on screen the pages stack; @media print they each fill an A4 landscape sheet. */
export default function Brochure() {
  return (
    <main className="bg-zinc-900 min-h-screen text-black" data-testid="brochure-page">
      {/* Screen-only controls */}
      <div className="print:hidden sticky top-0 z-50 bg-zinc-950 border-b border-white/5 px-4 py-3 flex items-center justify-between text-white">
        <Link to="/" className="inline-flex items-center gap-2 fl-mono text-[11px] tracking-widest uppercase text-slate-300 hover:text-emerald-500">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-emerald-500 hidden sm:block">
          // A4 tri-fold · print duplex, fold in thirds
        </div>
        <button onClick={() => window.print()} className="fl-btn !py-2 !px-3 text-xs" data-testid="brochure-print">
          <Printer className="w-4 h-4" /> Print
        </button>
      </div>

      <div className="print:hidden max-w-3xl mx-auto px-5 py-6 text-slate-300 text-sm space-y-2">
        <h1 className="text-white text-xl font-bold fl-display">Print your tri-fold brochure</h1>
        <p>
          Click <strong className="text-emerald-400">Print</strong>, choose <strong className="text-emerald-400">A4 landscape</strong> and <strong className="text-emerald-400">Two-sided</strong> (flip on long edge).
          Page 1 prints the back/inside-front/cover panels. Page 2 prints the three audience panels.
          Fold in thirds — left panel folds in first, then right panel over it.
        </p>
      </div>

      {/* ============ PAGE 1 (outer side of the brochure) ============ */}
      <Sheet>
        {/* Panel A — back cover (becomes the back when folded) */}
        <Panel side="back">
          <div className="h-full flex flex-col justify-between">
            <div>
              <div className="text-[8pt] tracking-[0.3em] uppercase text-emerald-700 fl-mono">// Tune in</div>
              <h2 className="fl-display text-[28pt] font-extrabold leading-[0.95] tracking-tighter mt-2">
                Anyone. <br />Anywhere. <br /><span className="text-emerald-600">On air.</span>
              </h2>
              <p className="text-[10pt] mt-3 leading-snug text-zinc-700">
                Fliboe FM lets independent broadcasters, artists, and local businesses launch their own internet radio station and reach listeners worldwide. No transmitter. No license. Just press <em>go</em>.
              </p>
            </div>
            <div className="space-y-3">
              <div className="border-2 border-emerald-500 rounded-lg p-3 bg-emerald-50">
                <div className="text-[8pt] tracking-widest uppercase text-emerald-700 fl-mono">// Scan to launch</div>
                <div className="flex items-center gap-3 mt-2">
                  <div className="bg-white p-1.5 border border-emerald-300 rounded">
                    <QRCodeSVG value="https://fliboe.com" size={88} level="M" includeMargin={false} />
                  </div>
                  <div className="text-[9pt] leading-snug">
                    <div className="font-bold text-zinc-900">fliboe.com</div>
                    <div className="text-zinc-600">Sign up free.<br />Live in 5 minutes.</div>
                  </div>
                </div>
              </div>
              <div className="text-[7pt] fl-mono uppercase tracking-widest text-zinc-500 text-center">
                © 2026 FLIBOE FM · Worldwide
              </div>
            </div>
          </div>
        </Panel>

        {/* Panel B — inside front (becomes interior when first opened) */}
        <Panel side="inside-front">
          <div className="h-full flex flex-col">
            <div className="text-[8pt] tracking-[0.3em] uppercase text-emerald-700 fl-mono">// What you get</div>
            <h3 className="fl-display text-[16pt] font-extrabold leading-tight tracking-tighter mt-1.5">A full radio station,<br />out of the box.</h3>
            <div className="grid grid-cols-1 gap-2.5 mt-4">
              <Feature icon={Mic2} title="AI DJ between every song" body="A friendly host introduces every track. Voices switch with time of day." />
              <Feature icon={Globe2} title="Worldwide sync engine" body="Everyone listening on every continent hears the same song at the same second." />
              <Feature icon={Coins} title="Tip Jar — 100% yours" body="PayPal, Cash App, Venmo. Tips go straight to you — Fliboe takes nothing." />
              <Feature icon={Smartphone} title="Installs on iOS & Android" body="One tap to add Fliboe FM to your phone — no app store needed." />
              <Feature icon={Bell} title="Push alerts" body="Listeners get a push when you go live or schedule a new show." />
              <Feature icon={Sparkles} title="One-click TikTok clips" body="Export branded 9:16 video clips of your show to Reels and Stories." />
            </div>
            <div className="mt-auto pt-3 text-[8pt] fl-mono uppercase tracking-widest text-zinc-500 text-right">
              // fold here →
            </div>
          </div>
        </Panel>

        {/* Panel C — front cover (the face people see when folded) */}
        <Panel side="cover" amber>
          <div className="h-full flex flex-col justify-between text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-black/30 border-2 border-white/40 flex items-center justify-center relative">
                  <Radio className="w-5 h-5" strokeWidth={2.4} />
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-emerald-500" />
                </div>
                <div className="leading-none">
                  <div className="fl-display text-[14pt] font-extrabold tracking-tighter">FLIBOE FM</div>
                  <div className="fl-mono text-[6pt] tracking-[0.3em] mt-0.5 opacity-80">/ ON AIR · WORLDWIDE</div>
                </div>
              </div>
            </div>
            <div>
              <div className="fl-display text-[38pt] font-extrabold leading-[0.9] tracking-tighter">Your own<br />radio station.</div>
              <div className="fl-display text-[24pt] font-extrabold leading-tight tracking-tighter mt-2 text-black/85">Worldwide.</div>
              <div className="mt-4 h-px bg-black/30" />
              <p className="mt-3 text-[11pt] leading-snug text-black/85 font-medium">
                For broadcasters, local businesses, artists, and influencers who want to own their audience.
              </p>
            </div>
            <div className="space-y-1.5">
              <div className="bg-black text-emerald-400 inline-block px-3 py-1.5 rounded-full text-[9pt] font-bold fl-mono tracking-[0.15em] uppercase">
                Free to launch
              </div>
              <div className="fl-mono text-[8pt] tracking-[0.25em] uppercase text-black/70">
                fliboe.com
              </div>
            </div>
          </div>
        </Panel>
      </Sheet>

      {/* ============ PAGE 2 (inside spread, three audiences) ============ */}
      <Sheet>
        <Panel side="audience-business">
          <Audience
            badge="01 · For local businesses"
            icon={Megaphone}
            title="Your own branded station — your loyalty channel"
            tagline="Gyms · salons · restaurants · car washes · churches · barbershops"
            bullets={[
              "Brand the station with your name, logo, jingles.",
              "Loop your own playlist + promo spots between songs.",
              "Embed the player on your website. Stream in-store and online.",
              "Listeners worldwide — turn customers into community.",
              "Replaces $40/mo background-music subscriptions.",
            ]}
            cta="Ask us about Broadcaster — $9.99/mo · 30-day pass · no contracts."
          />
        </Panel>

        <Panel side="audience-artist">
          <Audience
            badge="02 · For local artists"
            icon={Music}
            title="Get heard before the algorithm picks you"
            tagline="Indie musicians · producers · podcasters · talk-show hosts"
            bullets={[
              "Upload your tracks — the AI DJ hypes them up between plays.",
              "Schedule talk shows + listener Q&A.",
              "Tip Jar accepts PayPal · Cash App · Venmo. 100% yours.",
              "Worldwide sync = a real audience in real time, not just views.",
              "One-tap export to Reels, TikTok, Stories.",
            ]}
            cta="Premium — $4.99/mo unlocks the Tip Jar and HD audio."
          />
        </Panel>

        <Panel side="audience-influencer">
          <Audience
            badge="03 · For influencers + creators"
            icon={Star}
            title="A new lane your followers can't get on Insta"
            tagline="Coaches · commentators · culture hosts · DJs"
            bullets={[
              "AI DJ + custom personality keep your station on air 24/7.",
              "Push notifications bring fans back every show.",
              "Referral rewards: 3 sign-ups = 1 month Premium free.",
              "Embed your station on your link-in-bio page.",
              "Build an owned audience that doesn't disappear with an algorithm change.",
            ]}
            cta="Scan QR. Sign up free. Be on air in 5 minutes."
            qr
          />
        </Panel>
      </Sheet>
    </main>
  );
}

function Sheet({ children }) {
  return (
    <div
      className="brochure-sheet mx-auto my-6 bg-white shadow-2xl flex flex-row print:my-0 print:shadow-none"
      style={{ width: "297mm", height: "210mm" }}
    >
      {children}
    </div>
  );
}

function Panel({ children, amber }) {
  return (
    <section
      className={`flex-1 p-[10mm] border-r border-dashed border-black/15 last:border-r-0 overflow-hidden ${amber ? "bg-emerald-500" : ""}`}
      style={{ flexBasis: "99mm", maxWidth: "99mm" }}
    >
      {children}
    </section>
  );
}

function Feature({ icon: Icon, title, body }) {
  return (
    <div className="flex gap-2.5">
      <div className="shrink-0 w-7 h-7 rounded bg-emerald-100 border border-emerald-300 flex items-center justify-center">
        <Icon className="w-3.5 h-3.5 text-emerald-700" />
      </div>
      <div className="min-w-0">
        <div className="text-[9.5pt] font-bold leading-tight text-zinc-900">{title}</div>
        <div className="text-[8pt] text-zinc-600 leading-snug mt-0.5">{body}</div>
      </div>
    </div>
  );
}

function Audience({ badge, icon: Icon, title, tagline, bullets, cta, qr }) {
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded bg-emerald-500 text-white flex items-center justify-center">
          <Icon className="w-4 h-4" strokeWidth={2.4} />
        </div>
        <div className="text-[8pt] fl-mono tracking-[0.25em] uppercase text-emerald-700">{badge}</div>
      </div>
      <h3 className="fl-display text-[15pt] font-extrabold leading-tight tracking-tighter mt-2 text-zinc-900">{title}</h3>
      <div className="text-[8pt] uppercase tracking-widest text-zinc-500 fl-mono mt-1">{tagline}</div>
      <ul className="mt-3 space-y-1.5">
        {bullets.map((b) => (
          <li key={b} className="flex gap-1.5 text-[9pt] leading-snug text-zinc-800">
            <CheckCircle2 className="w-3 h-3 text-emerald-600 mt-0.5 shrink-0" /> {b}
          </li>
        ))}
      </ul>
      <div className="mt-auto pt-3">
        <div className="text-[8.5pt] bg-zinc-900 text-emerald-400 rounded p-2 fl-mono leading-snug">{cta}</div>
        {qr && (
          <div className="mt-3 flex items-center gap-2">
            <div className="bg-white p-1 border border-zinc-300 rounded">
              <QRCodeSVG value="https://fliboe.com" size={64} level="M" includeMargin={false} />
            </div>
            <div className="text-[8pt] leading-tight">
              <div className="font-bold text-zinc-900">fliboe.com</div>
              <div className="text-zinc-600">Sign up · launch · go live</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
